import type { Location, StockLevel } from '../../inventory/inventory.ts';

export type StockColumnChoices = {
  readonly added: ReadonlySet<string>;
  readonly dropped: ReadonlySet<string>;
};

export type StockColumnInputs = StockColumnChoices & {
  readonly locations: readonly Location[];
  readonly levels: readonly StockLevel[];
  readonly entered: Readonly<Record<string, string>>;
};

export const NO_CHOICES: StockColumnChoices = { added: new Set(), dropped: new Set() };

const WHOLE_NUMBER = /^\d+$/u;

export const quantityOf = (text: string | undefined): number => {
  const trimmed = text?.trim() ?? '';
  return WHOLE_NUMBER.test(trimmed) ? Number(trimmed) : 0;
};

export type StockAcceptance = (variantId: string | null) => boolean;

type StockRow = { readonly variantId: string | null; readonly enabled: boolean };

const availableAt = (levels: readonly StockLevel[], variantId: string | null, locationId: string): number =>
  levels.find((level) => level.variantId === variantId && level.locationId === locationId)?.available ?? 0;

export type DepotUnitsInput = {
  readonly rows: readonly StockRow[];
  readonly locationId: string;
  readonly levels: readonly StockLevel[];
  readonly accepts: StockAcceptance;
  readonly typed: string | undefined;
};

export const depotUnits = ({ rows, locationId, levels, accepts, typed }: DepotUnitsInput): number => {
  const sold = rows.filter((row) => row.enabled);
  const recorded = sold
    .filter((row) => !accepts(row.variantId))
    .reduce((sum, row) => sum + availableAt(levels, row.variantId, locationId), 0);
  return sold.some((row) => accepts(row.variantId)) ? recorded + quantityOf(typed) : recorded;
};

export const withoutDepot = <T>(
  entries: Readonly<Record<string, T>>,
  locationId: string,
): Record<string, T> => Object.fromEntries(Object.entries(entries).filter(([id]) => id !== locationId));

const holdsStock = (levels: readonly StockLevel[], locationId: string): boolean =>
  levels.some((level) => level.locationId === locationId);

const hasEntry = (entered: StockColumnInputs['entered'], locationId: string): boolean =>
  (entered[locationId] ?? '').trim() !== '';

export const isLockedColumn = (levels: readonly StockLevel[], location: Location): boolean =>
  location.isDefault || holdsStock(levels, location.id);

const byDefaultThenName = (left: Location, right: Location): number =>
  Number(right.isDefault) - Number(left.isDefault) || left.name.localeCompare(right.name);

export const stockColumnsOf = (inputs: StockColumnInputs): readonly Location[] =>
  inputs.locations
    .filter((location) => {
      if (isLockedColumn(inputs.levels, location)) return true;
      if (inputs.dropped.has(location.id)) return false;
      return inputs.added.has(location.id) || hasEntry(inputs.entered, location.id);
    })
    .toSorted(byDefaultThenName);

export const withColumnAdded = (choices: StockColumnChoices, locationId: string): StockColumnChoices => ({
  added: new Set([...choices.added, locationId]),
  dropped: new Set([...choices.dropped].filter((id) => id !== locationId)),
});

export const withColumnDropped = (choices: StockColumnChoices, locationId: string): StockColumnChoices => ({
  added: new Set([...choices.added].filter((id) => id !== locationId)),
  dropped: new Set([...choices.dropped, locationId]),
});
